import { useEffect, useMemo, useState } from 'react';
import { DataTableProps } from './utils/type';
import { useSorting } from '../../hooks/use-sorting.hook';
import { useFiltering } from '../../hooks/use-filtering.hook';
import { usePagination } from '../../hooks/use-pagination.hook';
import { Head } from './parts/head.component';
import { Body } from './parts/body.component';
import { Footer } from './parts/footer.component';
import { Pagination } from './parts/pagination.component';
import DataTableSkeleton from './parts/loader-tables.component';
import Icon from '../forms/icon.component';

declare const KTComponents: any;

export default function DataTable<T>(props: DataTableProps<T>) {
  const { columns, data, responsive = true, scrollY, footer } = props;
  const tableFrameHeight =
    scrollY ??
    (props.paging === false
      ? 'clamp(400px, calc(100dvh - 360px), 780px)'
      : 'clamp(525px, calc(100dvh - 405px), 900px)');

  /* =========================
   * FLAGS
   * ========================= */
  const loading = props.loading ?? props.isLoading ?? false;

  const searchEnabled =
    props.search?.enabled ?? (props.hideSearch !== undefined ? !props.hideSearch : true);

  const columnsToggleEnabled = props.hideColumns !== undefined ? props.hideColumns : true;

  /* =========================
   * STATE
   * ========================= */
  const [term, setTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(props.pagination?.rowsPerPage ?? 10);

  const [visibleColumns, setVisibleColumns] = useState<boolean[]>(() => columns.map(() => true));

  const [columnFilters, setColumnFilters] = useState<string[]>(() =>
    new Array(columns.length).fill('')
  );

  const toggleColumn = (index: number) => {
    setVisibleColumns((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  const visibleColumnIndexes = useMemo(
    () =>
      columns.reduce<number[]>((indexes, _, index) => {
        if (!columnsToggleEnabled || visibleColumns[index]) {
          indexes.push(index);
        }
        return indexes;
      }, []),
    [columns, columnsToggleEnabled, visibleColumns]
  );

  const visibleCols = useMemo(
    () => visibleColumnIndexes.map((index) => columns[index]),
    [columns, visibleColumnIndexes]
  );

  const visibleColumnFilters = useMemo(
    () => visibleColumnIndexes.map((index) => columnFilters[index] ?? ''),
    [columnFilters, visibleColumnIndexes]
  );

  /* =========================
   * SORT
   * ========================= */
  const sortingEnabled = props.sorting?.enabled ?? true;
  const sortingDefault = props.sorting?.default ?? props.order;

  const { sortedRows, sortConfig, setSort } = useSorting(
    data,
    columns,
    sortingEnabled,
    sortingDefault
  );

  /* =========================
   * FILTER
   * ========================= */
  const filteredRows = useFiltering(
    sortedRows,
    columns,
    term,
    props.search?.columnIndex ?? props.searchColumnIndex,
    columnFilters,
    props.search?.coincidence ?? props.searchCoincidence,
    selectedFilter,
    props.columnFilter,
    visibleColumnIndexes
  );

  /* =========================
   * PAGINATION
   * ========================= */
  const paginationEnabled =
    props.pagination?.enabled ?? (props.paging !== undefined ? props.paging : true);

  const { pagedRows, page, setPage, totalPages, isFirst, isLast } = usePagination(
    filteredRows,
    paginationEnabled,
    rowsPerPage
  );

  const rowsToRender = paginationEnabled ? pagedRows : filteredRows;
  const shouldStretchTable =
    Boolean(scrollY) ||
    rowsToRender.length === 0 ||
    (paginationEnabled && rowsToRender.length >= rowsPerPage);

  const visibleSortConfig = useMemo(() => {
    if (sortConfig.columnIndex === -1) return sortConfig;

    return {
      ...sortConfig,
      columnIndex: visibleColumnIndexes.indexOf(sortConfig.columnIndex)
    };
  }, [sortConfig, visibleColumnIndexes]);

  useEffect(() => {
    setPage(0);
  }, [term, columnFilters, selectedFilter]);

  const handleRowsPerPageChange = (newRpp: number) => {
    const firstVisibleIndex = page * rowsPerPage;
    setPage(Math.floor(firstVisibleIndex / newRpp));
    setRowsPerPage(newRpp);
  };

  useEffect(() => {
    KTComponents.init();
  });

  /* =========================
   * LOADING
   * ========================= */
  if (loading) {
    return (
      <DataTableSkeleton
        columns={columns.length}
        rows={rowsPerPage}
        showFilters={searchEnabled}
        showPagination={paginationEnabled}
      />
    );
  }

  return (
    <>
      {searchEnabled && (
        <div className="d-flex flex-column flex-md-row justify-content-between gap-3 mb-5 w-100">
          {/* LEFT */}
          <div className="position-relative w-100 w-md-auto">
            <Icon
              icon="ki-magnifier"
              size="fs-3"
              type="danger"
              className="position-absolute ms-4 mt-4"
            />
            <input
              type="text"
              className="form-control form-control-solid ps-12 w-100 w-md-250px b-radius"
              placeholder="Buscar..."
              value={term}
              onChange={(e) => setTerm(e.target.value)}
            />
          </div>

          {/* RIGHT */}
          <div className="d-flex flex-column flex-sm-row align-items-center gap-2">
            {columnsToggleEnabled && (
              <div className="dropdown">
                <button className="btn btn-sm btn-light w-100 b-radius" data-bs-toggle="dropdown">
                  <Icon icon="ki-switch" size="fs-1" className="me-2" />
                  Columnas
                </button>

                <div className="dropdown-menu dropdown-menu-end p-4 shadow rounded-4 w-250px force-scroll">
                  {columns.map((col, i) => (
                    <div key={i} className="d-flex align-items-center justify-content-between mb-3">
                      <span className="fw-semibold">{col.name}</span>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={visibleColumns[i]}
                          onChange={() => toggleColumn(i)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {props.headerActions}

            {props.filterGeneral && props.optionsFilter && (
              <select
                className="form-select form-select-solid"
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value)}
              >
                <option value="">Todos</option>
                {props.optionsFilter.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}

      {/* ================= TABLE ================= */}
      <div
        className={responsive ? 'table-responsive' : ''}
        style={{
          minHeight: shouldStretchTable ? tableFrameHeight : undefined,
          maxHeight: tableFrameHeight,
          overflowY: 'auto'
        }}
      >
        <table className="table table-row-bordered table-striped gs-3 mb-0">
          <Head
            columns={visibleCols}
            sortConfig={visibleSortConfig}
            filters={visibleColumnFilters}
            onSort={(i) => {
              const columnIndex = visibleColumnIndexes[i];
              if (!sortingEnabled || columnIndex === undefined) return;

              setSort((prev: any) =>
                prev.columnIndex === columnIndex
                  ? {
                      columnIndex,
                      direction: prev.direction === 'asc' ? 'desc' : 'asc'
                    }
                  : { columnIndex, direction: 'asc' }
              );
            }}
            onFilterChange={(i, v) => {
              const columnIndex = visibleColumnIndexes[i];
              if (columnIndex === undefined) return;

              setColumnFilters((prev) => {
                const next = [...prev];
                next[columnIndex] = v;
                return next;
              });
            }}
          />

          {rowsToRender.length ? (
            <Body
              rows={rowsToRender}
              columns={visibleCols}
              term={term}
              highlight={props.search?.coincidence ?? false}
            />
          ) : (
            <tbody>
              <tr>
                <td colSpan={visibleCols.length} className="p-0">
                  <div className="datatable-empty d-flex flex-column align-items-center justify-content-center text-center">
                    <div className="empty-icon-wrapper mb-4">
                      <Icon icon="ki-magnifier" size="fs-1" type="danger" />
                    </div>
                    <h5 className="fw-bolder mb-2">No encontramos el dato que busca</h5>
                    <span className="fs-7">Verifica tus filtros o intenta con otro dato</span>
                  </div>
                </td>
              </tr>
            </tbody>
          )}

          {(footer?.enabled ?? columns.some((c) => c.summable)) && (
            <Footer rows={filteredRows} columns={visibleCols} />
          )}
        </table>
      </div>

      {/* ================= PAGINATION ================= */}
      {paginationEnabled && filteredRows.length > 0 && (
        <Pagination
          currentPage={page}
          rowsPerPage={rowsPerPage}
          totalRows={filteredRows.length}
          isFirst={isFirst}
          isLast={isLast}
          onRowsPerPageChange={handleRowsPerPageChange}
          onChange={(a) =>
            a === 'first'
              ? setPage(0)
              : a === 'prev'
                ? setPage((p) => Math.max(p - 1, 0))
                : a === 'next'
                  ? setPage((p) => Math.min(p + 1, totalPages - 1))
                  : setPage(totalPages - 1)
          }
        />
      )}
    </>
  );
}
